/**
 * PersonalSettingsStore
 *
 * Manages in-memory personal settings. Persistence is handled by
 * personalSettingsAppData.ts using wildlark.settings/personal.v1.
 */

import { isThinkingLevel, type ThinkingLevel } from "../agentTarget"
import { action, makeObservable, observable, runInAction } from "mobx"
import type { SettingsTab } from "../components/settings/SettingsModal"
import { normalizeGitDiffStrategy, type GitDiffStrategy } from "../utils/gitDiffStrategy"
import { normalizeNewTaskPinnedAgents, type NewTaskAgentTarget } from "./newTaskAgentPins"

// ============================================================================
// Types
// ============================================================================

/**
 * Theme setting options.
 * - "system": Follow OS preference (prefers-color-scheme)
 * - "code-theme-*": Always use a specific theme
 *
 * Legacy persisted values "light" and "dark" are normalized at store creation.
 * Additional themes can be added here in the future.
 */

/** Theme metadata for display in UI */
interface ThemeInfo {
    /** Display name shown in settings */
    label: string
}

/** Actual theme class names (CSS classes applied to root element) with metadata */
export const themeClasses = {
    "code-theme-light": { label: "Light" },
    "code-theme-bright": { label: "Bright" },
    "code-theme-clean": { label: "Clean" },
    "code-theme-black": { label: "Black" },
    "code-theme-synthwave": { label: "Synthwave" },
    "code-theme-dracula": { label: "Dracula" },
} as const satisfies Record<string, ThemeInfo>

/** Actual theme class type (excludes "system") */
export type ThemeClass = keyof typeof themeClasses

/** All available theme settings including "system" */
export const availableThemeClasses = {
    system: true,
    ...themeClasses,
} as const

/** Theme setting that can be stored (includes "system" option) */
export type ThemeSetting = keyof typeof availableThemeClasses

/** Ordered list of all theme options for UI display (system first, then theme classes in order) */
export const allThemeOptions: Array<{ value: ThemeSetting; label: string; isSystem: boolean }> = [
    { value: "system", label: "System", isSystem: true },
    ...(Object.keys(themeClasses) as ThemeClass[]).map((key) => ({
        value: key as ThemeSetting,
        label: themeClasses[key].label,
        isSystem: false,
    })),
]

/** Default theme to use when system preference is dark */
export const defaultDarkTheme: ThemeClass = "code-theme-black"
/** Default theme to use when system preference is light */
export const defaultLightTheme: ThemeClass = "code-theme-light"

export const GLOBAL_SYSTEM_PROMPT_MAX_LENGTH = 32_000

export type PermissionModeSetting = "ask" | "auto" | "yolo"

export const permissionModeOptions: Array<{ value: PermissionModeSetting; label: string; description: string }> = [
    { value: "ask", label: "Ask every time", description: "Pause for approval before provider operations that require permission." },
    { value: "auto", label: "Automatic review", description: "Let the provider review operations and ask only when it escalates or blocks one." },
    { value: "yolo", label: "Unrestricted", description: "Bypass provider approval prompts for writable turns." },
]

function normalizePermissionMode(value: unknown): PermissionModeSetting {
    return value === "ask" || value === "auto" || value === "yolo" ? value : "yolo"
}

/**
 * Personal settings stored per-user.
 */
export interface PersonalSettings {
    /** Non-secret voice preference; the API key stays in the native vault. */
    voiceName?: string
    /** Custom environment variables to propagate to all subprocesses, Claude queries, and PTYs */
    envVars: Record<string, string>
    /** Birth-time instructions copied into every new task and its child agent threads. */
    globalSystemPrompt: string
    /** Writable-turn authorization policy. Legacy settings preserve unrestricted behavior. */
    permissionMode: PermissionModeSetting
    /** Theme preference - defaults to "system" */
    theme: ThemeSetting
    /** Last viewed settings tab - remembers which tab to open by default */
    lastSettingsTab?: SettingsTab
    /** Anonymous device ID for analytics - generated on first launch */
    deviceId?: string
    /** Opt-out of telemetry - undefined/false means enabled, true means disabled */
    telemetryDisabled?: boolean
    /** Whether the user has completed onboarding - shows only once per device */
    onboardingCompleted?: boolean
    /** Dev: Hide the tray buttons and slide-out panel */
    devHideTray?: boolean
    /** Render assistant markdown as formatted prose instead of source-style text. */
    renderMarkdownMessages?: boolean
    /** Include test files in Git Log working-copy and commit reviews across every task. */
    showTestFiles: boolean
    gitDiffStrategy: GitDiffStrategy
    /** Version string of the latest release notes the user has seen */
    lastSeenReleaseVersion?: string
    /** Last harness selected on the new task page; validated before use. */
    newTaskHarnessId?: string
    /** Last model selected on the new task page; validated against newTaskHarnessId before use. */
    newTaskModelId?: string
    /** Thinking level paired with the last new-task model selection. */
    newTaskThinking?: ThinkingLevel
    /** Ordered user-authored quick agents. Undefined uses the product defaults; an empty list is intentional. */
    newTaskPinnedAgents?: NewTaskAgentTarget[]

    /** Task IDs the user has pinned to the top of the sidebar */
    pinnedTaskIds?: string[]
    /** How this person appears to colleagues in share invites and on their computers. */
    profile?: PersonalProfile
}

export interface PersonalProfile {
    name?: string
    glyph?: string
    color?: string
}

export function normalizePersonalProfile(value: unknown): PersonalProfile | undefined {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return undefined
    const record = value as Record<string, unknown>
    const profile: PersonalProfile = {}
    if (typeof record.name === "string" && record.name.trim()) profile.name = record.name.trim().slice(0, 80)
    if (typeof record.glyph === "string" && record.glyph.trim()) profile.glyph = record.glyph.trim().slice(0, 4)
    if (typeof record.color === "string" && record.color.trim()) profile.color = record.color.trim().slice(0, 40)
    return Object.keys(profile).length > 0 ? profile : undefined
}

export type PersonalSettingsInput = Partial<Omit<PersonalSettings, "theme">> & {
    theme?: ThemeSetting | "light" | "dark"
}

/**
 * PersonalSettingsStore manages user-specific settings.
 */
export interface PersonalSettingsStore {
    settings: PersonalSettingsHandle
}

export interface PersonalSettingsHandle {
    current: PersonalSettings
    get(): PersonalSettings
    set(partial: Partial<PersonalSettings>): void
    update(recipe: (draft: PersonalSettings) => void): void
    subscribe(callback: () => void): () => void
}

function defaultPersonalSettings(): PersonalSettings {
    return {
        envVars: {},
        globalSystemPrompt: "",
        permissionMode: "yolo",
        theme: "system",
        renderMarkdownMessages: true,
        showTestFiles: false,
        gitDiffStrategy: "current",
    }
}

export function normalizePersonalSettings(settings: PersonalSettingsInput | undefined): PersonalSettings {
    const withDefaults = {
        ...defaultPersonalSettings(),
        ...settings,
    }
    const storedTheme = withDefaults.theme
    let theme: ThemeSetting
    if (storedTheme === "light") {
        theme = defaultLightTheme
    } else if (storedTheme === "dark") {
        theme = defaultDarkTheme
    } else if (storedTheme === undefined) {
        theme = "system"
    } else {
        theme = storedTheme
    }
    const normalized: PersonalSettings = {
        ...withDefaults,
        globalSystemPrompt: typeof withDefaults.globalSystemPrompt === "string" ? withDefaults.globalSystemPrompt : "",
        permissionMode: normalizePermissionMode(withDefaults.permissionMode),
        theme,
        renderMarkdownMessages: withDefaults.renderMarkdownMessages ?? true,
        showTestFiles: withDefaults.showTestFiles ?? false,
        gitDiffStrategy: normalizeGitDiffStrategy(withDefaults.gitDiffStrategy),
    }
    if (withDefaults.newTaskPinnedAgents !== undefined) normalized.newTaskPinnedAgents = normalizeNewTaskPinnedAgents(withDefaults.newTaskPinnedAgents)
    if (!isThinkingLevel(normalized.newTaskThinking)) delete normalized.newTaskThinking
    const profile = normalizePersonalProfile(withDefaults.profile)
    if (profile) normalized.profile = profile
    else delete normalized.profile
    return normalized
}

export type PersonalSettingsChangeHandler = (settings: PersonalSettings, patch: Partial<PersonalSettings>) => void

function cloneSettings<T>(value: T): T {
    return globalThis.structuredClone(value)
}

function changedSettingsPatch(previous: PersonalSettings, next: PersonalSettings): Partial<PersonalSettings> {
    const patch: Partial<PersonalSettings> = {}
    const keys = new Set([...Object.keys(previous), ...Object.keys(next)] as Array<keyof PersonalSettings>)
    for (const key of keys) {
        if (!Object.is(previous[key], next[key])) {
            Object.assign(patch, { [key]: next[key] })
        }
    }
    return patch
}

export function createPersonalSettingsStoreFromValue(initial?: PersonalSettingsInput, onChange?: PersonalSettingsChangeHandler): PersonalSettingsStore {
    const subscribers = new Set<() => void>()
    const notify = (patch: Partial<PersonalSettings>) => {
        onChange?.(handle.current, patch)
        for (const subscriber of subscribers) subscriber()
    }
    const handle: PersonalSettingsHandle = {
        current: normalizePersonalSettings(initial),

        get(): PersonalSettings {
            return this.current
        },

        set(partial: Partial<PersonalSettings>): void {
            this.current = normalizePersonalSettings({ ...this.current, ...partial })
            notify(partial)
        },

        update(recipe: (draft: PersonalSettings) => void): void {
            const previous = this.current
            const draft = cloneSettings(this.current)
            recipe(draft)
            this.current = normalizePersonalSettings(draft)
            notify(changedSettingsPatch(previous, this.current))
        },

        subscribe(callback: () => void): () => void {
            subscribers.add(callback)
            return () => {
                subscribers.delete(callback)
            }
        },
    }

    makeObservable(handle, {
        current: observable.ref,
        set: action,
        update: action,
    })

    return { settings: handle }
}

export function replacePersonalSettings(store: PersonalSettingsStore, settings: PersonalSettingsInput): void {
    runInAction(() => {
        store.settings.current = normalizePersonalSettings(settings)
    })
}

/**
 * Creates an in-memory PersonalSettingsStore. AppData callers should use
 * createPersonalSettingsStoreFromValue() with the parsed persisted record.
 */
export function createPersonalSettingsStore(initial?: PersonalSettingsInput): PersonalSettingsStore {
    return createPersonalSettingsStoreFromValue(initial)
}
